import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'
import KeyboardEventHandler from 'react-keyboard-event-handler'

import {
  MenuBookRounded as PanelIcon,
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  KeyboardRounded as ShortcutIcon,
  ForumRounded as MyContentIcon,
  ChatBubbleRounded as FAQIcon,
  LibraryBooks,
} from '@material-ui/icons'

import { CircularProgress } from '@material-ui/core'

import SearchField from '../../atoms/inputs/searchField'
import IconButton from '../../atoms/inputs/iconButton'
import Icon from '../../atoms/display/icon'
import Typography from '../../atoms/display/typography'

import useStyles from './treeOfKnowledgeStyle'

import Shortcuts from '../../molecules/treeOfKnowledge/shortcuts'
import MyContent from '../../molecules/treeOfKnowledge/myContent'
import Faqs from '../../molecules/treeOfKnowledge/faqs'
import Scripts from '../../molecules/treeOfKnowledge/scripts'
import { removeSpecials } from '../../../utils/customMethods'
import { setKeywordSearch, setTabOpen } from '../../../redux/faq/actions'

const handleKeys = [
  'up',
  'down',
  'enter',
  'ctrl+enter',
  'alt+1',
  'alt+2',
  'alt+3',
  'alt+4',
]

const TreeOfKnowledge = (props) => {
  var undock = useSelector((state) => state.undock)

  const { t } = useTranslation()

  let useStylesParams = {
    undock: undock,
  }

  const classes = useStyles(useStylesParams)
  const dispatch = useDispatch()
  const theme = useTheme()

  const shortcuts = useSelector((state) => state.shortcuts)
  const myContent = useSelector((state) => state.myContent)
  const faqs = useSelector((state) => state.faq)
  const activeConversation = useSelector((state) => state.activeConversation)
  const buttonValue = faqs.tabOpen?.[activeConversation] || 0
  const loadingFaqs =
    faqs?.status?.get_faq?.loading || faqs?.status?.get_faq_by_skill?.loading

  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )

  const keyword = useSelector(
    (state) => state.faq?.keyword?.[activeConversation]
  )

  const [numberResults, setNumberResults] = useState(null)

  const inputSearchRef = useRef()
  const totalAnchorsRef = useRef(0)
  const currentAnchorRef = useRef(0)
  const anchorsRef = useRef([])

  const handleKeyword = (value) => {
    if (value === keyword) return
    dispatch(setKeywordSearch(activeConversation, value))
  }

  const renderEmptySpace = () => {
    return (
      <>
        <div className={classes.emptySpace}>
          <Icon>
            <PanelIcon />
          </Icon>
          <Typography variant="body1" style={{ marginTop: 10 }}>
            {t('commons.treeOfKnowledge')}
          </Typography>
          <Typography
            variant="body2"
            style={{ color: theme.palette.highlightSearchActive }}
          >
            {t('commons.emptyActiveConversation')}
          </Typography>
        </div>
      </>
    )
  }

  const search = () => {
    if (inputSearchRef?.current?.value) {
      handleKeyword(removeSpecials(inputSearchRef.current.value))
      setTimeout(() => {
        const x = document.getElementsByClassName(`anchor-faq`)
        for (let i = 0; i < x.length; i++) {
          x[i].style.backgroundColor = theme.palette.highlightSearchActive
          x[i].style.color = theme.palette.text.secondary
        }
        const elements = document.getElementsByClassName(`search-anchor-tree`)
        totalAnchorsRef.current = anchorsRef.current.length
        currentAnchorRef.current = 0
        if (elements.length > 0) {
          elements[0].style.borderColor = theme.palette.primary.main
          elements[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        }
        anchorsRef.current = elements
        setNumberResults({
          total: elements.length,
          current: elements.length ? 1 : 0,
        })
        inputSearchRef.current?.focus()
      }, 500)
    } else {
      handleKeyword('')
      setNumberResults(null)
    }
  }

  const jumpToSearchAnchor = (moviment) => {
    const x = anchorsRef.current
    if (keyword === '' || !numberResults?.total) return
    totalAnchorsRef.current = x.length
    for (let i = 0; i < x.length; i++) {
      x[i].style.borderColor = 'transparent'
    }
    if (currentAnchorRef.current === 0 && moviment === 'up') {
      currentAnchorRef.current = x.length - 1
    } else if (
      currentAnchorRef.current === x.length - 1 &&
      moviment === 'down'
    ) {
      currentAnchorRef.current = 0
    } else {
      currentAnchorRef.current =
        moviment === 'up'
          ? currentAnchorRef.current - 1
          : currentAnchorRef.current + 1
    }
    setNumberResults((prev) => ({
      total: prev?.total || x.length,
      current: currentAnchorRef.current + 1 || 1,
    }))
    if (x[currentAnchorRef.current]) {
      x[currentAnchorRef.current].style.borderColor = theme.palette.primary.main
      x[currentAnchorRef.current].scrollIntoView()
    }
  }

  const RenderContent = useCallback(() => {
    if (conversation) {
      switch (buttonValue) {
        case 0:
          return <Shortcuts keyword={keyword} />
        case 1:
          return <MyContent keyword={keyword} />
        case 2:
          return <Faqs keyword={keyword} />
        case 3:
          return <Scripts keyword={keyword} />
        default:
          return renderEmptySpace()
      }
    } else {
      return renderEmptySpace()
    }
  }, [buttonValue, conversation, keyword])

  const handleKeyDown = (key) => {
    switch (key) {
      case 'up':
        jumpToSearchAnchor('up')
        break
      case 'down':
        jumpToSearchAnchor('down')
        break
      case 'enter':
        search()
        break
      case 'ctrl+enter':
        if (anchorsRef.current[currentAnchorRef.current]) {
          anchorsRef.current[currentAnchorRef.current].click()
        }
        break
      case 'alt+1':
        handleTabOpen(0)
        break
      case 'alt+2':
        handleTabOpen(1)
        break
      case 'alt+3':
        handleTabOpen(2)
        break
      case 'alt+4':
        handleTabOpen(3)
        break
      default:
        break
    }
  }

  const handleTabOpen = (value) => {
    dispatch(setTabOpen(activeConversation, value))
  }

  useEffect(() => {
    if (keyword) {
      inputSearchRef.current.value = keyword
    }
    search()
  }, [buttonValue, activeConversation, keyword])

  return (
    <>
      <KeyboardEventHandler handleKeys={handleKeys} onKeyEvent={handleKeyDown}>
        <div className={classes.root}>
          <div className={classes.right}>
            <div className={classes.rightHeader}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Icon text={t('commons.treeOfKnowledge')}>
                  {shortcuts?.status?.get_shortcuts?.loading ||
                  myContent?.status?.get_my_content?.loading ||
                  loadingFaqs ? (
                    <CircularProgress size={24} />
                  ) : (
                    <PanelIcon />
                  )}
                </Icon>
              </div>
            </div>
            <div className={classes.search}>
              <SearchField
                id="field-search"
                label={t('fields.search.label')}
                placeholder={t('fields.search.placeHolder')}
                type="text"
                color="primary"
                inputRef={inputSearchRef}
                variant="outlined"
                name="search"
                shrink={true}
                fullWidth={true}
                autoComplete="off"
                defaultValue={keyword}
              />
              {numberResults && (
                <p
                  className={classes.numberResults}
                >{`${numberResults?.current}/${numberResults?.total}`}</p>
              )}
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <div style={{ width: 10 }} />
                <IconButton
                  color="primary"
                  onClick={() => jumpToSearchAnchor('up')}
                  disabled={!numberResults?.total}
                >
                  <UpIcon style={{ color: theme.palette.primary.main }} />
                </IconButton>
                <div style={{ width: 10 }} />
                <IconButton
                  color="primary"
                  onClick={() => jumpToSearchAnchor('down')}
                  disabled={!numberResults?.total}
                >
                  <DownIcon style={{ color: theme.palette.primary.main }} />
                </IconButton>
              </div>
            </div>
            <div className={classes.buttons}>
              <button
                className={
                  buttonValue === 0 ? classes.buttonActive : classes.button
                }
                style={{ marginRight: 15 }}
                onClick={() => {
                  handleTabOpen(0)
                }}
              >
                <div>
                  <ShortcutIcon className={classes.buttonIcon} />
                </div>
                <Typography variant="body2">
                  {t('buttons.shortcuts')}
                </Typography>
              </button>
              <button
                className={
                  buttonValue === 1 ? classes.buttonActive : classes.button
                }
                style={{ marginRight: 15 }}
                onClick={() => {
                  handleTabOpen(1)
                }}
              >
                <div>
                  <MyContentIcon className={classes.buttonIcon} />
                </div>
                <Typography variant="body2" style={{ textAlign: 'center' }}>
                  {t('buttons.myContent')}
                </Typography>
              </button>
              <button
                className={
                  buttonValue === 2 ? classes.buttonActive : classes.button
                }
                style={{ marginRight: 15 }}
                onClick={() => {
                  handleTabOpen(2)
                }}
              >
                <div>
                  <FAQIcon className={classes.buttonIcon} />
                </div>
                <Typography variant="body2">{t('buttons.FAQ')}</Typography>
              </button>
              <button
                className={
                  buttonValue === 3 ? classes.buttonActive : classes.button
                }
                style={{ marginRight: 15 }}
                onClick={() => {
                  handleTabOpen(3)
                }}
              >
                <div>
                  <LibraryBooks className={classes.buttonIcon} />
                </div>
                <Typography variant="body2">{t('buttons.script')}</Typography>
              </button>
            </div>
            <div className={classes.content}>
              <RenderContent />
            </div>
          </div>
        </div>
      </KeyboardEventHandler>
    </>
  )
}

export default TreeOfKnowledge
